import { useState } from 'react';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { site } from '@shared/site.config';
import type { LeadInterest } from '@shared/types';

const INTEREST_OPTIONS: { value: LeadInterest; label: string }[] = [
  { value: 'buying-selling', label: 'Buying or selling' },
  { value: 'leasing', label: 'Leasing' },
  { value: 'business-listing', label: 'Business listing' },
  { value: 'general', label: 'General inquiry' },
];

interface Props {
  defaultInterest?: LeadInterest;
  defaultMessage?: string;
  /** Heading shown above the fields; pass null to render fields only. */
  title?: string | null;
  subtitle?: string;
}

/**
 * Fields carry visible annotation labels rather than placeholder-only text —
 * it suits the drawing language, and a placeholder that vanishes on focus is
 * a worse form regardless of the aesthetic.
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="annot">{label}</span>
      <span className="mt-1.5 block">{children}</span>
    </label>
  );
}

export default function ContactForm({
  defaultInterest = 'general',
  defaultMessage = '',
  title = 'Tell us what you need',
  subtitle,
}: Props) {
  const { pathname, search } = useLocation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: defaultMessage,
    interest: defaultInterest,
    _gotcha: '', // honeypot — real people never see or fill this
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError('');
    try {
      await api.submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        message: form.message.trim(),
        interest: form.interest as LeadInterest,
        sourcePath: pathname + search,
        _gotcha: form._gotcha || undefined,
      });
      setState('sent');
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (state === 'sent') {
    return (
      <div className="sheet-panel sheet-marked shadow-sheet p-8 text-center">
        <div className="mx-auto mb-5 grid h-11 w-11 place-items-center border border-redline text-redline">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <p className="annot">Issued</p>
        <h3 className="mt-2 font-display text-[24px]">Message sent</h3>
        <p className="mt-3 text-[14px] leading-relaxed text-annot">
          It goes straight to {site.agent.shortName} — not a call center. Expect a reply the same day, usually
          within a couple of hours.
        </p>
        <a
          href={`tel:${site.agent.phone.replace(/\D/g, '')}`}
          className="mt-6 inline-block font-mono text-[10px] uppercase tracking-wider2 text-redline hover:text-redline-600"
        >
          Or call {site.agent.phone} now
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="sheet-panel shadow-sheet p-6 sm:p-7">
      {title && (
        <div className="mb-6 border-b border-hairline pb-5">
          <h3 className="font-display text-[24px] leading-tight">{title}</h3>
          {subtitle && <p className="mt-1.5 text-[14px] leading-relaxed text-annot">{subtitle}</p>}
        </div>
      )}

      <div className="space-y-4">
        <Field label="Interest">
          <select value={form.interest} onChange={set('interest')} className="field">
            {INTEREST_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <input required value={form.name} onChange={set('name')} className="field" autoComplete="name" />
          </Field>
          <Field label="Company — optional">
            <input value={form.company} onChange={set('company')} className="field" autoComplete="organization" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email">
            <input required type="email" value={form.email} onChange={set('email')} className="field" autoComplete="email" />
          </Field>
          <Field label="Phone — optional">
            <input value={form.phone} onChange={set('phone')} className="field" autoComplete="tel" />
          </Field>
        </div>

        <Field label="What are you working on?">
          <textarea required rows={4} value={form.message} onChange={set('message')} className="field resize-y" />
        </Field>

        {/* Honeypot. `hidden` (not off-screen CSS) keeps Chrome/Edge business-profile
            autofill from filling it — that autofill ignores position but respects `hidden`. */}
        <input
          type="text"
          name="_gotcha"
          value={form._gotcha}
          onChange={set('_gotcha')}
          tabIndex={-1}
          autoComplete="off"
          hidden
        />
      </div>

      {state === 'error' && (
        <div className="mt-4 border-l-2 border-redline bg-redline-100/50 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Not sent</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-graphite-600">
            {error} You can also call {site.agent.phone} directly.
          </p>
        </div>
      )}

      <button type="submit" disabled={state === 'sending'} className="btn-primary mt-6 w-full">
        {state === 'sending' ? 'Sending…' : 'Send message'}
      </button>

      <p className="mt-4 text-center text-[12px] leading-relaxed text-annot">
        Goes directly to {site.agent.name}. No newsletter, no drip campaign, no third parties.
      </p>
    </form>
  );
}
